import { ChangeEvent, CSSProperties, UIEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ClipboardList,
  Download,
  FileSpreadsheet,
  PackagePlus,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Trash2,
  UsersRound
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabaseClient } from '../lib/supabaseClient';
import { downloadCsvBlob, toCsvLine } from '../lib/csvUtils';
import { compareResidentsByKana, hasResidentKana } from '../lib/residentSort';

type SupplyItemKind = 'charge' | 'descriptor';

type SupplyItem = {
  id: string;
  dbId?: string;
  sortOrder?: number;
  name: string;
  unitPrice: number;
  kind: SupplyItemKind;
  active: boolean;
};

type SupplySalesRow = {
  id: string;
  dbId?: string;
  displayOrder: number;
  residentName: string;
  residentKana?: string;
  residentId?: string;
  residentStatus?: 'active' | 'retired' | 'inactive';
  departureDate?: string;
  active?: boolean;
  values: Record<string, string>;
  memo: string;
  source: 'excel' | 'resident-master' | 'manual';
};

type SupplySalesState = {
  month: string;
  monthLabel: string;
  items: SupplyItem[];
  rows: SupplySalesRow[];
  updatedAt: string;
};

type CellKind = 'blank' | 'circle' | 'amount' | 'note' | 'descriptor';
type CellEditMode = 'blank' | 'circle' | 'amount' | 'self' | 'hold' | 'note' | 'large' | 'medium' | 'small';
type CellPrimaryMode = 'blank' | 'circle' | 'other' | 'large' | 'medium' | 'small';
type CellOtherMode = 'amount' | 'self' | 'hold' | 'note';
type DetailTab = 'summary' | 'issues' | 'settings';
type ResidentView = 'current' | 'retired';

type CellEvaluation = {
  kind: CellKind;
  amount: number;
};

type SupplyItemDbRow = {
  id: string;
  item_key: string;
  sort_order: number;
  item_name: string;
  unit_price: number | string;
  item_kind: SupplyItemKind;
  visible: boolean;
  active: boolean;
};

type SupplyMonthDbRow = {
  id: string;
  month_key: string;
  month_label: string;
  source_sheet_name: string | null;
};

type SupplySalesDbRow = {
  id: string;
  row_key: string;
  resident_id: string | null;
  resident_name_snapshot: string;
  resident_kana_snapshot: string | null;
  resident_status_snapshot: SupplySalesRow['residentStatus'] | null;
  resident_departure_date_snapshot: string | null;
  display_order: number;
  memo: string | null;
  source: SupplySalesRow['source'];
  active: boolean;
};

type SupplySalesCellDbRow = {
  row_id: string;
  item_id: string;
  value_text: string | null;
};

const CIRCLE_VALUES = new Set(['〇', '○', '◯']);
const RESIDENT_COLUMN_WIDTH = 224;
const ITEM_COLUMN_WIDTH = 160;
const TOTAL_COLUMN_WIDTH = 112;
const MEMO_COLUMN_WIDTH = 256;

const currentMonthKey = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const initialItems: SupplyItem[] = [
  { id: 'snack', name: 'おやつ', unitPrice: 3500, kind: 'charge', active: true },
  { id: 'thickenerKitchen', name: 'とろみ厨房', unitPrice: 1020, kind: 'charge', active: true },
  { id: 'thickenerDining', name: 'とろみダイニング', unitPrice: 0, kind: 'descriptor', active: true },
  { id: 'pocari', name: 'ポカリ', unitPrice: 510, kind: 'charge', active: true },
  { id: 'polident', name: 'ポリデント', unitPrice: 306, kind: 'charge', active: true },
  { id: 'deodorizer', name: 'ポータ消臭液', unitPrice: 714, kind: 'charge', active: true },
  { id: 'shampoo', name: 'シャンプー', unitPrice: 500, kind: 'charge', active: true },
  { id: 'cableTv', name: 'ケーブルtv', unitPrice: 1100, kind: 'charge', active: true },
  { id: 'laundrySeal', name: '洗濯シール', unitPrice: 1500, kind: 'charge', active: true },
  { id: 'razor', name: '剃刀', unitPrice: 500, kind: 'charge', active: true }
];

const initialRows: SupplySalesRow[] = [
  { id: 'excel-1', displayOrder: 1, residentName: '石川　千代子', values: { snack: '〇', polident: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-2', displayOrder: 2, residentName: '石橋 文', values: {}, memo: '12/30退院後オムツ使用', source: 'excel' },
  { id: 'excel-3', displayOrder: 3, residentName: '板垣　朝夫', values: { snack: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-4', displayOrder: 4, residentName: '岩井　暎枝', values: { snack: '〇', deodorizer: '714' }, memo: '2/10入院4/24退院', source: 'excel' },
  { id: 'excel-5', displayOrder: 5, residentName: '上野　洋子', values: { snack: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-6', displayOrder: 6, residentName: '内田　昌司', values: { snack: '〇', shampoo: '1月請求', razor: '○' }, memo: '２０２５/１２入居', source: 'excel' },
  { id: 'excel-7', displayOrder: 7, residentName: '内山　フク子', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-9', displayOrder: 9, residentName: '小倉　カツ様', values: { snack: '〇', pocari: '〇', deodorizer: '1500', shampoo: '1月請求' }, memo: '2026.1.2入居　5/23Ｐトイレ', source: 'excel' },
  { id: 'excel-10', displayOrder: 10, residentName: '柿沼　泰子', values: { snack: '〇', thickenerKitchen: '2040', thickenerDining: '大' }, memo: '', source: 'excel' },
  { id: 'excel-11', displayOrder: 11, residentName: '笠井　静江', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-12', displayOrder: 12, residentName: '川上　良子', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-13', displayOrder: 13, residentName: '北村　訓子', values: { snack: '〇', pocari: '自分' }, memo: '', source: 'excel' },
  { id: 'excel-14', displayOrder: 14, residentName: '木村　美津江', values: { snack: '〇', pocari: '〇', polident: '○' }, memo: '', source: 'excel' },
  { id: 'excel-15', displayOrder: 15, residentName: '小久保セイ子', values: { snack: '〇', polident: '〇' }, memo: '6/3入院　6/１４退院', source: 'excel' },
  { id: 'excel-17', displayOrder: 17, residentName: '齋藤　美津江', values: { snack: '○', pocari: '○', polident: '〇', shampoo: '2月請求' }, memo: '2/20入居5/6~ポリデント', source: 'excel' },
  { id: 'excel-18', displayOrder: 18, residentName: '坂詰　隆', values: { snack: '〇', pocari: '〇', razor: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-19', displayOrder: 19, residentName: '坂詰　由己子', values: { snack: '〇', pocari: '〇', polident: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-20', displayOrder: 20, residentName: '柴﨑　千鶴子', values: { snack: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-21', displayOrder: 21, residentName: '鈴木　富子', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-22', displayOrder: 22, residentName: '鈴木　良勝', values: { snack: '〇', shampoo: '1月請求', razor: '○' }, memo: '.1月入居 4/2入院4/13退院', source: 'excel' },
  { id: 'excel-24', displayOrder: 24, residentName: '関口　雅子', values: { snack: '〇', pocari: '〇', polident: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-25', displayOrder: 25, residentName: '高田　克子', values: { snack: '〇' }, memo: 'シャンプーのみ個人', source: 'excel' },
  { id: 'excel-26', displayOrder: 26, residentName: '高畑　ひろ子', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-27', displayOrder: 27, residentName: '武田　和子', values: { snack: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-28', displayOrder: 28, residentName: '武田　俊喜', values: { snack: '〇', deodorizer: '1500' }, memo: '', source: 'excel' },
  { id: 'excel-29', displayOrder: 29, residentName: '田島　シゲ', values: { snack: '〇', polident: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-31', displayOrder: 31, residentName: '辻　糸子', values: { snack: '〇', thickenerDining: '小' }, memo: '12/31退院オムツへ', source: 'excel' },
  { id: 'excel-32', displayOrder: 32, residentName: '辻　昇一', values: { snack: '〇', polident: '〇', razor: '○' }, memo: '', source: 'excel' },
  { id: 'excel-34', displayOrder: 34, residentName: '中嶋　三惠', values: { snack: '〇', deodorizer: '714', shampoo: '㋇済' }, memo: '', source: 'excel' },
  { id: 'excel-35', displayOrder: 35, residentName: '長島　八重子', values: { snack: '〇', pocari: '〇', polident: '〇' }, memo: '３/１７～ポリデントさつきにて', source: 'excel' },
  { id: 'excel-36', displayOrder: 36, residentName: '蓮　まさえ', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-37', displayOrder: 37, residentName: '服部　多佳子', values: { snack: '〇', thickenerKitchen: '2040', thickenerDining: '大', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-38', displayOrder: 38, residentName: '林　善子', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-39', displayOrder: 39, residentName: '馬場　良一', values: { snack: '〇', pocari: '〇', polident: '〇', razor: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-40', displayOrder: 40, residentName: '藤井　芳江', values: { snack: '〇', thickenerKitchen: '2040', thickenerDining: '大', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-41', displayOrder: 41, residentName: '本田　正衛', values: { snack: '〇', pocari: '自分', polident: '〇', deodorizer: '1500', razor: '○' }, memo: '', source: 'excel' },
  { id: 'excel-42', displayOrder: 42, residentName: '丸山　俊幸', values: { snack: '○', thickenerKitchen: '○', thickenerDining: '大', shampoo: '自分', razor: '○' }, memo: '2026.2.5入居', source: 'excel' },
  { id: 'excel-43', displayOrder: 43, residentName: '三木　忠久', values: { snack: '〇', thickenerKitchen: '2040', thickenerDining: '中', pocari: '〇', razor: '〇' }, memo: '3/11入院 5/25退院', source: 'excel' },
  { id: 'excel-44', displayOrder: 44, residentName: '水野　冨美子', values: { snack: '○', pocari: '○', shampoo: '1月請求' }, memo: '2026.1.23入居', source: 'excel' },
  { id: 'excel-45', displayOrder: 45, residentName: '村社　健之', values: { snack: '〇', polident: '〇', deodorizer: '保留', cableTv: '1100', razor: '〇' }, memo: '6月～ポリデント使用', source: 'excel' },
  { id: 'excel-46', displayOrder: 46, residentName: '森田　邦江', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-47', displayOrder: 47, residentName: '米田　葉子', values: { snack: '〇', pocari: '〇' }, memo: '', source: 'excel' },
  { id: 'excel-48', displayOrder: 48, residentName: '渡邉　鉄三', values: { snack: '〇' }, memo: '', source: 'excel' }
];

const createInitialState = (): SupplySalesState => ({
  month: currentMonthKey(),
  monthLabel: `生活用品 ${currentMonthKey()}`,
  items: initialItems,
  rows: initialRows,
  updatedAt: new Date().toISOString()
});

const mapDbItem = (row: SupplyItemDbRow): SupplyItem => ({
  id: row.item_key,
  dbId: row.id,
  sortOrder: row.sort_order,
  name: row.item_name,
  unitPrice: Number(row.unit_price) || 0,
  kind: row.item_kind,
  active: row.visible
});

const mapDbRow = (row: SupplySalesDbRow, values: Record<string, string>): SupplySalesRow => ({
  id: row.row_key,
  dbId: row.id,
  displayOrder: row.display_order,
  residentId: row.resident_id ?? undefined,
  residentName: row.resident_name_snapshot,
  residentKana: row.resident_kana_snapshot ?? undefined,
  residentStatus: row.resident_status_snapshot ?? undefined,
  departureDate: row.resident_departure_date_snapshot ?? undefined,
  active: row.active,
  values,
  memo: row.memo ?? '',
  source: row.source
});

const itemToDbPatch = (patch: Partial<SupplyItem>) => {
  const payload: Record<string, string | number | boolean> = {};
  if (patch.name !== undefined) payload.item_name = patch.name;
  if (patch.unitPrice !== undefined) payload.unit_price = patch.unitPrice;
  if (patch.kind !== undefined) payload.item_kind = patch.kind;
  if (patch.active !== undefined) payload.visible = patch.active;
  if (patch.sortOrder !== undefined) payload.sort_order = patch.sortOrder;
  return payload;
};

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 }).format(value);

const formatDepartureDate = (value?: string): string => value ? value.slice(0, 10).replaceAll('-', '/') : '日付未設定';

const normalizeName = (name: string): string =>
  name
    .normalize('NFKC')
    .replace(/様$/g, '')
    .replace(/[ 　\t\r\n]/g, '')
    .replace(/髙/g, '高')
    .replace(/﨑/g, '崎')
    .replace(/冨/g, '富')
    .replace(/惠/g, '恵')
    .toLowerCase();

const parseNumericAmount = (raw: string): number | null => {
  const normalized = raw.replace(/,/g, '').replace(/[０-９]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xfee0));
  if (!/^\d+$/.test(normalized)) return null;
  return Number(normalized);
};

const evaluateCell = (item: SupplyItem, rawValue: string | undefined): CellEvaluation => {
  const value = (rawValue ?? '').trim();
  if (!value) return { kind: 'blank', amount: 0 };
  if (item.kind === 'descriptor') return { kind: 'descriptor', amount: 0 };
  if (CIRCLE_VALUES.has(value)) return { kind: 'circle', amount: item.unitPrice };
  const numeric = parseNumericAmount(value);
  if (numeric !== null) return { kind: 'amount', amount: numeric };
  return { kind: 'note', amount: 0 };
};

const rowTotal = (row: SupplySalesRow, items: SupplyItem[]): number =>
  items.reduce((sum, item) => sum + evaluateCell(item, row.values[item.id]).amount, 0);

const getCellClass = (evaluation: CellEvaluation): string => {
  if (evaluation.kind === 'circle') return 'border-emerald-200 bg-emerald-50 text-emerald-900';
  if (evaluation.kind === 'amount') return 'border-sky-200 bg-sky-50 text-sky-900';
  if (evaluation.kind === 'descriptor') return 'border-violet-200 bg-violet-50 text-violet-900';
  if (evaluation.kind === 'note') return 'border-amber-200 bg-amber-50 text-amber-900';
  return 'border-gray-200 bg-white text-gray-800';
};

const getCellKey = (rowId: string, itemId: string) => `${rowId}:${itemId}`;

const inferCellMode = (item: SupplyItem, rawValue: string | undefined): CellEditMode => {
  const value = (rawValue ?? '').trim();
  if (!value) return 'blank';
  if (item.kind === 'descriptor') {
    if (value === '大') return 'large';
    if (value === '中') return 'medium';
    if (value === '小') return 'small';
    return 'note';
  }
  if (CIRCLE_VALUES.has(value)) return 'circle';
  if (parseNumericAmount(value) !== null) return 'amount';
  if (value === '自分') return 'self';
  if (value === '保留') return 'hold';
  return 'note';
};

const shouldShowAmountInput = (mode: CellEditMode) => mode === 'amount';
const shouldShowNoteInput = (mode: CellEditMode) => mode === 'note';

const noteInputValue = (item: SupplyItem, value: string, mode: CellEditMode) => {
  if (mode !== 'note') return '';
  const inferred = inferCellMode(item, value);
  return inferred === 'note' ? value : '';
};

const getPrimaryMode = (item: SupplyItem, mode: CellEditMode): CellPrimaryMode => {
  if (item.kind === 'descriptor') {
    if (mode === 'large' || mode === 'medium' || mode === 'small' || mode === 'blank') return mode;
    return 'other';
  }
  if (mode === 'blank' || mode === 'circle') return mode;
  return 'other';
};

const getOtherMode = (mode: CellEditMode): CellOtherMode => {
  if (mode === 'amount' || mode === 'self' || mode === 'hold' || mode === 'note') return mode;
  return 'amount';
};

const nextMonthValue = (month: string): string => {
  const [year, monthIndex] = month.split('-').map(Number);
  if (!year || !monthIndex) return month;
  const date = new Date(year, monthIndex, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const SupplySalesPage = () => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<SupplySalesState>(createInitialState);
  const [availableMonths, setAvailableMonths] = useState<SupplyMonthDbRow[]>([]);
  const [query, setQuery] = useState('');
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('summary');
  const [residentView, setResidentView] = useState<ResidentView>('current');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [cellModes, setCellModes] = useState<Record<string, CellEditMode>>({});
  const [horizontalViewportWidth, setHorizontalViewportWidth] = useState(ITEM_COLUMN_WIDTH);
  const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previousTitle = document.title;
    document.title = '物品販売・月末請求 | さつきホーム';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const markSavedSoon = () => {
    setSaveStatus('saved');
    window.setTimeout(() => setSaveStatus('idle'), 1600);
  };

  const loadSupplyData = async (requestedMonthKey?: string) => {
    if (!supabaseClient) {
      setLoadError('Supabase接続設定が見つかりません。初期データを表示しています。');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const { data: monthRows, error: monthListError } = await supabaseClient
        .from('supply_sales_months')
        .select('id, month_key, month_label, source_sheet_name')
        .eq('active', true)
        .order('month_key', { ascending: false });
      if (monthListError) throw monthListError;

      const months = (monthRows ?? []) as SupplyMonthDbRow[];
      if (months.length === 0) throw new Error('利用できる対象月がありません。');
      setAvailableMonths(months);

      const preferredMonthKey = requestedMonthKey ?? currentMonthKey();
      const month = months.find(current => current.month_key === preferredMonthKey) ?? months[0];

      const { error: reconcileError } = await supabaseClient.rpc('supply_sales_reconcile_month', {
        _month_key: month.month_key
      });
      if (reconcileError) throw reconcileError;

      const { data: itemData, error: itemError } = await supabaseClient
        .from('supply_sales_items')
        .select('id, item_key, sort_order, item_name, unit_price, item_kind, visible, active')
        .eq('active', true)
        .order('sort_order', { ascending: true });
      if (itemError) throw itemError;

      const { data: rowData, error: rowError } = await supabaseClient
        .from('supply_sales_rows')
        .select('id, row_key, resident_id, resident_name_snapshot, resident_kana_snapshot, resident_status_snapshot, resident_departure_date_snapshot, display_order, memo, source, active')
        .eq('month_id', month.id)
        .order('display_order', { ascending: true });
      if (rowError) throw rowError;

      const items = ((itemData ?? []) as SupplyItemDbRow[]).map(mapDbItem);
      const rows = (rowData ?? []) as SupplySalesDbRow[];
      const rowIds = rows.map(row => row.id);
      const cellValuesByRow = new Map<string, Record<string, string>>();
      const itemKeyByDbId = new Map(items.map(item => [item.dbId, item.id]));

      if (rowIds.length > 0) {
        const { data: cellData, error: cellError } = await supabaseClient
          .from('supply_sales_cells')
          .select('row_id, item_id, value_text')
          .in('row_id', rowIds);
        if (cellError) throw cellError;

        ((cellData ?? []) as SupplySalesCellDbRow[]).forEach(cell => {
          const itemKey = itemKeyByDbId.get(cell.item_id);
          if (!itemKey) return;
          const current = cellValuesByRow.get(cell.row_id) ?? {};
          current[itemKey] = cell.value_text ?? '';
          cellValuesByRow.set(cell.row_id, current);
        });
      }

      setState({
        month: month.month_key,
        monthLabel: month.month_label,
        items,
        rows: rows.map(row => mapDbRow(row, cellValuesByRow.get(row.id) ?? {})),
        updatedAt: new Date().toISOString()
      });
      setCellModes({});
      setResidentView('current');
      setSaveStatus('idle');
    } catch (error: any) {
      setLoadError(`物品販売データの取得に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSupplyData(searchParams.get('month') ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeItems = useMemo(() => state.items.filter(item => item.active), [state.items]);
  const chargeItems = useMemo(() => activeItems.filter(item => item.kind === 'charge'), [activeItems]);
  const scrollableTableWidth = activeItems.length * ITEM_COLUMN_WIDTH + TOTAL_COLUMN_WIDTH + MEMO_COLUMN_WIDTH;
  const tableWidth = RESIDENT_COLUMN_WIDTH + scrollableTableWidth;
  const maxHorizontalScroll = Math.max(0, scrollableTableWidth - horizontalViewportWidth);
  const horizontalThumbWidth = Math.max(
    56,
    Math.min(horizontalViewportWidth, horizontalViewportWidth ** 2 / scrollableTableWidth)
  );
  const currentRows = useMemo(() => state.rows.filter(row => row.active !== false), [state.rows]);
  const retiredRows = useMemo(
    () => state.rows.filter(row => row.active === false && row.residentStatus === 'retired'),
    [state.rows]
  );
  const sortedRowsForView = useMemo(() => {
    const source = residentView === 'current' ? currentRows : retiredRows;
    return [...source].sort((left, right) => compareResidentsByKana(
      { name: left.residentName, kana: left.residentKana, displayOrder: left.displayOrder },
      { name: right.residentName, kana: right.residentKana, displayOrder: right.displayOrder }
    ));
  }, [currentRows, residentView, retiredRows]);
  const rowNumberById = useMemo(
    () => new Map(sortedRowsForView.map((row, index) => [row.id, index + 1])),
    [sortedRowsForView]
  );

  const filteredRows = useMemo(() => {
    const normalizedQuery = normalizeName(query);
    const requestedResidentId = searchParams.get('resident');
    return sortedRowsForView
      .filter(row => !requestedResidentId || row.residentId === requestedResidentId)
      .filter(row => !normalizedQuery || normalizeName(row.residentName).includes(normalizedQuery));
  }, [query, searchParams, sortedRowsForView]);

  useEffect(() => {
    const tableScroll = tableScrollRef.current;
    if (!tableScroll) return;

    const updateHorizontalViewportWidth = () => {
      setHorizontalViewportWidth(Math.max(ITEM_COLUMN_WIDTH, tableScroll.clientWidth - RESIDENT_COLUMN_WIDTH));
    };

    updateHorizontalViewportWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHorizontalViewportWidth);
      return () => window.removeEventListener('resize', updateHorizontalViewportWidth);
    }

    const observer = new ResizeObserver(updateHorizontalViewportWidth);
    observer.observe(tableScroll);
    return () => observer.disconnect();
  }, []);

  const syncTopHorizontalScroll = (event: UIEvent<HTMLDivElement>) => {
    setHorizontalScrollLeft(event.currentTarget.scrollLeft);
  };

  const syncTableHorizontalScroll = (event: ChangeEvent<HTMLInputElement>) => {
    const nextScrollLeft = Number(event.currentTarget.value);
    setHorizontalScrollLeft(nextScrollLeft);
    const tableScroll = tableScrollRef.current;
    if (tableScroll && tableScroll.scrollLeft !== nextScrollLeft) {
      tableScroll.scrollLeft = nextScrollLeft;
    }
  };

  const cellIssues = useMemo(() => {
    const issues: Array<{ row: SupplySalesRow; item: SupplyItem; value: string }> = [];
    currentRows.forEach(row => {
      chargeItems.forEach(item => {
        const value = (row.values[item.id] ?? '').trim();
        const evaluation = evaluateCell(item, value);
        if (evaluation.kind === 'note') issues.push({ row, item, value });
      });
    });
    return issues;
  }, [chargeItems, currentRows]);

  const summary = useMemo(() => {
    const itemTotals = chargeItems.map(item => {
      let amount = 0;
      let count = 0;
      currentRows.forEach(row => {
        const evaluated = evaluateCell(item, row.values[item.id]);
        if (evaluated.amount > 0) count += 1;
        amount += evaluated.amount;
      });
      return { item, amount, count };
    });
    const residentTotals = currentRows.map(row => ({ row, amount: rowTotal(row, chargeItems) }));
    return {
      total: residentTotals.reduce((sum, current) => sum + current.amount, 0),
      billedResidents: residentTotals.filter(current => current.amount > 0).length,
      itemTotals,
      residentTotals
    };
  }, [chargeItems, currentRows]);

  const persistCell = async (rowId: string, itemId: string, value: string) => {
    const row = state.rows.find(current => current.id === rowId);
    const item = state.items.find(current => current.id === itemId);
    if (!supabaseClient || !row?.dbId || !item?.dbId) {
      setSaveStatus('error');
      setLoadError('DB保存に必要な行または品目IDが見つかりません。');
      return;
    }

    setSaveStatus('saving');
    const { error } = await supabaseClient
      .from('supply_sales_cells')
      .upsert(
        {
          row_id: row.dbId,
          item_id: item.dbId,
          value_text: value
        },
        { onConflict: 'row_id,item_id' }
      );

    if (error) {
      setSaveStatus('error');
      setLoadError(`セルの保存に失敗しました: ${error.message}`);
      return;
    }

    setLoadError(null);
    markSavedSoon();
  };

  const updateCell = (rowId: string, itemId: string, value: string) => {
    setState(prev => ({
      ...prev,
      updatedAt: new Date().toISOString(),
      rows: prev.rows.map(row =>
        row.id === rowId
          ? {
              ...row,
              values: {
                ...row.values,
                [itemId]: value
              }
            }
          : row
      )
    }));
    void persistCell(rowId, itemId, value);
  };

  const updateCellPrimaryMode = (rowId: string, item: SupplyItem, mode: CellPrimaryMode, currentValue: string) => {
    const key = getCellKey(rowId, item.id);

    if (item.kind === 'descriptor') {
      if (mode === 'blank') {
        setCellModes(prev => ({ ...prev, [key]: 'blank' }));
        updateCell(rowId, item.id, '');
      }
      if (mode === 'large') {
        setCellModes(prev => ({ ...prev, [key]: 'large' }));
        updateCell(rowId, item.id, '大');
      }
      if (mode === 'medium') {
        setCellModes(prev => ({ ...prev, [key]: 'medium' }));
        updateCell(rowId, item.id, '中');
      }
      if (mode === 'small') {
        setCellModes(prev => ({ ...prev, [key]: 'small' }));
        updateCell(rowId, item.id, '小');
      }
      if (mode === 'other') {
        setCellModes(prev => ({ ...prev, [key]: 'note' }));
        if (inferCellMode(item, currentValue) !== 'note') updateCell(rowId, item.id, '');
      }
      return;
    }

    if (mode === 'blank') {
      setCellModes(prev => ({ ...prev, [key]: 'blank' }));
      updateCell(rowId, item.id, '');
    }
    if (mode === 'circle') {
      setCellModes(prev => ({ ...prev, [key]: 'circle' }));
      updateCell(rowId, item.id, '〇');
    }
    if (mode === 'other') {
      const inferred = inferCellMode(item, currentValue);
      const nextMode: CellOtherMode =
        inferred === 'amount' || inferred === 'self' || inferred === 'hold' || inferred === 'note'
          ? inferred
          : 'amount';
      setCellModes(prev => ({ ...prev, [key]: nextMode }));
      if (nextMode === 'amount' && parseNumericAmount(currentValue) === null) updateCell(rowId, item.id, '');
    }
  };

  const updateCellOtherMode = (rowId: string, item: SupplyItem, mode: CellOtherMode, currentValue: string) => {
    const key = getCellKey(rowId, item.id);
    setCellModes(prev => ({ ...prev, [key]: mode }));

    if (item.kind === 'descriptor') {
      if (mode === 'note' && inferCellMode(item, currentValue) !== 'note') updateCell(rowId, item.id, '');
      return;
    }

    if (mode === 'amount' && parseNumericAmount(currentValue) === null) updateCell(rowId, item.id, '');
    if (mode === 'self') updateCell(rowId, item.id, '自分');
    if (mode === 'hold') updateCell(rowId, item.id, '保留');
    if (mode === 'note' && inferCellMode(item, currentValue) !== 'note') updateCell(rowId, item.id, '');
  };

  const updateMemo = (rowId: string, memo: string) => {
    setState(prev => ({
      ...prev,
      updatedAt: new Date().toISOString(),
      rows: prev.rows.map(row => (row.id === rowId ? { ...row, memo } : row))
    }));
    const row = state.rows.find(current => current.id === rowId);
    if (!supabaseClient || !row?.dbId) return;
    setSaveStatus('saving');
    void supabaseClient
      .from('supply_sales_rows')
      .update({ memo })
      .eq('id', row.dbId)
      .then(({ error }) => {
        if (error) {
          setSaveStatus('error');
          setLoadError(`備考の保存に失敗しました: ${error.message}`);
          return;
        }
        setLoadError(null);
        markSavedSoon();
      });
  };

  const updateItem = (itemId: string, patch: Partial<SupplyItem>) => {
    setState(prev => ({
      ...prev,
      updatedAt: new Date().toISOString(),
      items: prev.items.map(item => (item.id === itemId ? { ...item, ...patch } : item))
    }));
    const item = state.items.find(current => current.id === itemId);
    if (!supabaseClient || !item?.dbId) return;
    const payload = itemToDbPatch(patch);
    if (Object.keys(payload).length === 0) return;
    setSaveStatus('saving');
    void supabaseClient
      .from('supply_sales_items')
      .update(payload)
      .eq('id', item.dbId)
      .then(({ error }) => {
        if (error) {
          setSaveStatus('error');
          setLoadError(`品目設定の保存に失敗しました: ${error.message}`);
          return;
        }
        setLoadError(null);
        markSavedSoon();
      });
  };

  const addItem = async () => {
    const name = window.prompt('追加する品目名を入力してください');
    if (!name?.trim()) return;
    const priceText = window.prompt('単価を入力してください（集計対象外なら0）', '0');
    const unitPrice = Number(priceText ?? '0');
    const maxSortOrder = state.items.reduce((max, item) => Math.max(max, item.sortOrder ?? 0), 0);
    const item: SupplyItem = {
      id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      sortOrder: maxSortOrder + 10,
      name: name.trim(),
      unitPrice: Number.isFinite(unitPrice) ? Math.max(0, unitPrice) : 0,
      kind: Number(priceText ?? '0') > 0 ? 'charge' : 'descriptor',
      active: true
    };

    if (supabaseClient) {
      setSaveStatus('saving');
      const { data, error } = await supabaseClient
        .from('supply_sales_items')
        .insert({
          item_key: item.id,
          sort_order: item.sortOrder,
          item_name: item.name,
          unit_price: item.unitPrice,
          item_kind: item.kind,
          visible: true,
          active: true
        })
        .select('id, item_key, sort_order, item_name, unit_price, item_kind, visible, active')
        .single();
      if (error) {
        setSaveStatus('error');
        setLoadError(`品目の追加に失敗しました: ${error.message}`);
        return;
      }
      Object.assign(item, mapDbItem(data as SupplyItemDbRow));
      setLoadError(null);
      markSavedSoon();
    }

    setState(prev => ({
      ...prev,
      updatedAt: new Date().toISOString(),
      items: [...prev.items, item]
    }));
  };

  const deleteItem = async (item: SupplyItem) => {
    const ok = window.confirm(`${item.name} を品目一覧から削除しますか？過去の入力値はDBに残し、今後の表では非表示にします。`);
    if (!ok) return;

    setState(prev => ({
      ...prev,
      updatedAt: new Date().toISOString(),
      items: prev.items.filter(current => current.id !== item.id)
    }));

    if (!supabaseClient || !item.dbId) return;
    setSaveStatus('saving');
    const { error } = await supabaseClient
      .from('supply_sales_items')
      .update({ active: false })
      .eq('id', item.dbId);

    if (error) {
      setSaveStatus('error');
      setLoadError(`品目の削除に失敗しました: ${error.message}`);
      await loadSupplyData(state.month);
      return;
    }
    setLoadError(null);
    markSavedSoon();
  };

  const duplicateMonth = async () => {
    const next = nextMonthValue(state.month);
    const month = window.prompt('作成する月を YYYY-MM 形式で入力してください', next);
    if (!month?.trim()) return;
    if (!supabaseClient) return;
    setSaveStatus('saving');
    const targetMonth = month.trim();
    const { error } = await supabaseClient.rpc('supply_sales_duplicate_month', {
      _source_month_key: state.month,
      _target_month_key: targetMonth,
      _target_month_label: `生活用品 ${targetMonth}`
    });
    if (error) {
      setSaveStatus('error');
      setLoadError(`月コピーに失敗しました: ${error.message}`);
      return;
    }
    markSavedSoon();
    await loadSupplyData(targetMonth);
  };

  const resetToExcel = () => {
    void loadSupplyData(state.month);
    setSyncStatus('DBから最新データを再読み込みしました。');
  };

  const downloadBillingCsv = () => {
    const header = [
      '対象月',
      '通し番号',
      '利用者名',
      ...activeItems.map(item => `${item.name}${item.kind === 'charge' ? `(${item.unitPrice}円)` : ''}`),
      '請求合計',
      '備考'
    ];
    const rows = [...currentRows]
      .sort((left, right) => compareResidentsByKana(
        { name: left.residentName, kana: left.residentKana, displayOrder: left.displayOrder },
        { name: right.residentName, kana: right.residentKana, displayOrder: right.displayOrder }
      ))
      .map((row, index) => [
        state.month,
        index + 1,
        row.residentName,
        ...activeItems.map(item => row.values[item.id] ?? ''),
        rowTotal(row, chargeItems),
        row.memo
      ]);
    downloadCsvBlob([header, ...rows].map(toCsvLine).join('\n'), `satsuki-supplies-${state.month}.csv`);
  };

  const copySummary = async () => {
    const lines = [
      `${state.monthLabel} 月末請求集計`,
      `請求合計: ${formatCurrency(summary.total)}`,
      `請求あり: ${summary.billedResidents}名 / ${currentRows.length}名`,
      '',
      ...summary.itemTotals.map(total => `・${total.item.name}: ${formatCurrency(total.amount)}（${total.count}名）`)
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setSyncStatus('月末集計をコピーしました。');
    } catch {
      setSyncStatus('コピーに失敗しました。CSV出力を使ってください。');
    }
  };

  const detailPanel = (
    <section className="overflow-hidden rounded-lg border border-[#d7ded4] bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-white">
        <nav className="flex gap-8 overflow-x-auto px-4" aria-label="物品販売詳細タブ">
          <button
            type="button"
            onClick={() => setDetailTab('summary')}
            className={`h-12 whitespace-nowrap border-b-2 px-1 text-sm font-semibold transition ${
              detailTab === 'summary'
                ? 'border-emerald-700 text-emerald-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            集計
          </button>
          <button
            type="button"
            onClick={() => setDetailTab('issues')}
            className={`h-12 whitespace-nowrap border-b-2 px-1 text-sm font-semibold transition ${
              detailTab === 'issues'
                ? 'border-emerald-700 text-emerald-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            要確認
          </button>
          <button
            type="button"
            onClick={() => setDetailTab('settings')}
            className={`h-12 whitespace-nowrap border-b-2 px-1 text-sm font-semibold transition ${
              detailTab === 'settings'
                ? 'border-emerald-700 text-emerald-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            品目・単価
          </button>
        </nav>
      </div>

      {detailTab === 'summary' && (
        <div className="grid gap-2 p-4 md:grid-cols-2 xl:grid-cols-3">
          {summary.itemTotals.map(total => (
            <div key={total.item.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
              <div>
                <div className="font-bold text-gray-900">{total.item.name}</div>
                <div className="mt-0.5 text-xs text-gray-500">{total.count}名</div>
              </div>
              <div className="font-bold text-gray-900">{formatCurrency(total.amount)}</div>
            </div>
          ))}
        </div>
      )}

      {detailTab === 'issues' && (
        <div className="bg-amber-50">
          <div className="flex items-start gap-3 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />
            <div className="min-w-0 text-sm text-amber-950">
              <div className="font-bold">要確認セル</div>
              <div className="mt-1 leading-6">「自分」「保留」「1月請求」など、請求額にしない入力を確認できます。</div>
            </div>
          </div>
          <div className="grid max-h-[360px] gap-2 overflow-auto border-t border-amber-200 bg-white/55 p-4 md:grid-cols-2 xl:grid-cols-3">
            {cellIssues.length === 0 && <div className="rounded-lg border border-amber-100 bg-white px-4 py-3 text-sm text-amber-900">要確認はありません。</div>}
            {cellIssues.map(issue => (
              <div key={`${issue.row.id}-${issue.item.id}`} className="rounded-lg border border-amber-100 bg-white px-4 py-3 text-sm">
                <div className="font-bold text-gray-900">{issue.row.residentName}</div>
                <div className="mt-1 text-amber-900">{issue.item.name}: {issue.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {detailTab === 'settings' && (
        <div className="p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-bold text-gray-900">
              <Settings className="h-5 w-5 text-emerald-700" />
              品目・単価設定
            </div>
            <button onClick={addItem} className="inline-flex h-9 items-center gap-2 rounded-lg border border-emerald-700 bg-emerald-700 px-3 text-xs font-bold text-white transition hover:bg-emerald-800">
              <Plus className="h-4 w-4" />
              品目追加
            </button>
          </div>
          <div className="grid gap-2 lg:grid-cols-2">
            {state.items.map(item => (
              <div key={item.id} className="grid grid-cols-[1fr_84px_64px_40px] gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
                <input
                  value={item.name}
                  onChange={event => updateItem(item.id, { name: event.target.value })}
                  className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm font-semibold outline-none focus:border-emerald-600"
                />
                <input
                  type="number"
                  value={item.unitPrice}
                  disabled={item.kind === 'descriptor'}
                  onChange={event => updateItem(item.id, { unitPrice: Math.max(0, Number(event.target.value) || 0) })}
                  className="h-9 rounded-md border border-gray-300 bg-white px-2 text-right text-sm font-semibold outline-none focus:border-emerald-600 disabled:bg-gray-100"
                />
                <label className="flex h-9 items-center justify-center gap-1 rounded-md border border-gray-300 bg-white text-xs font-semibold text-gray-600">
                  <input
                    type="checkbox"
                    checked={item.active}
                    onChange={event => updateItem(item.id, { active: event.target.checked })}
                  />
                  表示
                </label>
                <button
                  type="button"
                  onClick={() => void deleteItem(item)}
                  className="grid h-9 place-items-center rounded-md border border-red-200 bg-white text-red-600 transition hover:bg-red-50"
                  title="品目を削除"
                  aria-label={`${item.name}を削除`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );

  return (
    <main className="min-h-screen bg-[#f6f7f4] text-gray-900">
      <section className="border-b border-[#d7ded4] bg-[#20342f] text-white">
        <div className="mx-auto max-w-[1680px] px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white">さつきホーム</span>
                <span className="inline-flex items-center rounded-full border border-emerald-300/40 bg-emerald-300/15 px-3 py-1 text-xs font-semibold text-emerald-100">DB保存</span>
                <span className="inline-flex items-center rounded-full border border-sky-300/40 bg-sky-300/15 px-3 py-1 text-xs font-semibold text-sky-100">入居者マスター自動連動</span>
                <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                  {saveStatus === 'saving' && '保存中'}
                  {saveStatus === 'saved' && '保存済み'}
                  {saveStatus === 'error' && '保存エラー'}
                  {saveStatus === 'idle' && '本番データ'}
                </span>
                {loading && <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white">読み込み中</span>}
                <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white">元データ: 生活用品R8年６月</span>
              </div>
              <h1 className="text-2xl font-bold tracking-normal sm:text-3xl">物品販売・月末請求</h1>
              <p className="mt-2 text-sm text-white/75">日用品の利用チェック、月末集計、請求CSV出力をまとめて確認できます。入退居は入居者管理から自動反映されます。</p>
            </div>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <SummaryTile label="請求合計" value={formatCurrency(summary.total)} tone="bg-emerald-400/15 text-emerald-50 border-emerald-300/30" />
              <SummaryTile label="請求あり" value={`${summary.billedResidents}名`} tone="bg-sky-400/15 text-sky-50 border-sky-300/30" />
              <SummaryTile label="要確認" value={`${cellIssues.length}件`} tone="bg-amber-400/15 text-amber-50 border-amber-300/30" />
              <SummaryTile label="対象者" value={`${currentRows.length}名`} tone="bg-white/10 text-white border-white/20" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[#d7ded4] bg-white">
        <div className="mx-auto flex max-w-[1680px] flex-col gap-3 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <Link to="/care" className="inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">
              <ArrowLeft className="h-4 w-4" />
              ケアトップ
            </Link>
            {searchParams.get('resident') && (
              <Link to={`/admin/residents?resident=${searchParams.get('resident')}&detail=supplies`} className="inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">
                <UsersRound className="h-4 w-4" />
                入居者管理に戻る
              </Link>
            )}
            <Link to="/inventory" className="inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">
              <PackagePlus className="h-4 w-4" />
              在庫管理
            </Link>
            <div className="relative w-full md:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                className="h-11 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                placeholder="利用者名で検索"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700">
              <span className="whitespace-nowrap">対象月</span>
              <select
                value={state.month}
                onChange={event => void loadSupplyData(event.target.value)}
                className="min-w-[116px] bg-transparent text-sm font-semibold outline-none"
                aria-label="対象月"
              >
                {availableMonths.map(month => (
                  <option key={month.id} value={month.month_key}>{month.month_key}</option>
                ))}
              </select>
            </label>
            <button onClick={copySummary} className="inline-flex h-11 items-center gap-2 rounded-lg border border-[#20342f] bg-[#20342f] px-3 text-sm font-semibold text-white transition hover:bg-[#2b4740]">
              <ClipboardList className="h-4 w-4" />
              集計コピー
            </button>
            <button onClick={downloadBillingCsv} className="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">
              <Download className="h-4 w-4" />
              請求CSV
            </button>
            <button onClick={duplicateMonth} className="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">
              <FileSpreadsheet className="h-4 w-4" />
              月コピー
            </button>
            <button onClick={resetToExcel} disabled={loading} className="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              最新情報
            </button>
          </div>
        </div>
      </section>

      {loadError && (
        <div className="mx-auto max-w-[1680px] px-4 pt-4 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{loadError}</div>
        </div>
      )}

      {syncStatus && (
        <div className="mx-auto max-w-[1680px] px-4 pt-4 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900">{syncStatus}</div>
        </div>
      )}

      <div className="mx-auto max-w-[1680px] space-y-5 px-4 py-5 sm:px-6 lg:px-8">
        {detailPanel}

        <section className="overflow-hidden rounded-lg border border-[#d7ded4] bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 pt-3">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
              <div className="flex items-center gap-2">
                <UsersRound className="h-5 w-5 text-emerald-700" />
                <div>
                  <h2 className="text-base font-bold text-gray-900">{state.monthLabel}</h2>
                  <p className="mt-0.5 text-xs text-gray-500">退居月は最終請求の対象、翌月から退居者へ自動移動します。</p>
                </div>
              </div>
              <span className="text-sm text-gray-500">{filteredRows.length}名表示・50音順</span>
            </div>
            <nav className="flex gap-6" aria-label="入居状況">
              <button
                type="button"
                onClick={() => setResidentView('current')}
                className={`h-10 border-b-2 px-1 text-sm font-semibold ${residentView === 'current' ? 'border-emerald-700 text-emerald-700' : 'border-transparent text-gray-500'}`}
              >
                請求対象 {currentRows.length}
              </button>
              <button
                type="button"
                onClick={() => setResidentView('retired')}
                className={`h-10 border-b-2 px-1 text-sm font-semibold ${residentView === 'retired' ? 'border-emerald-700 text-emerald-700' : 'border-transparent text-gray-500'}`}
              >
                退居者 {retiredRows.length}
              </button>
            </nav>
          </div>
          <div
            ref={tableScrollRef}
            onScroll={syncTopHorizontalScroll}
            className="max-h-[72vh] min-h-[420px] overflow-auto overscroll-contain"
            data-testid="supply-table-scroll"
          >
            <table
              className="table-fixed border-collapse text-sm"
              style={{ width: tableWidth, minWidth: tableWidth }}
              data-testid="supply-data-table"
            >
              <colgroup>
                <col style={{ width: RESIDENT_COLUMN_WIDTH }} />
                {activeItems.map(item => <col key={item.id} style={{ width: ITEM_COLUMN_WIDTH }} />)}
                <col style={{ width: TOTAL_COLUMN_WIDTH }} />
                <col style={{ width: MEMO_COLUMN_WIDTH }} />
              </colgroup>
              <thead className="sticky top-0 z-30 bg-[#f0f3ee] text-xs font-semibold text-gray-600 shadow-[0_1px_0_#d7ded4]">
                <tr>
                  <th className="sticky left-0 top-0 z-40 border-r border-gray-200 bg-[#f0f3ee] px-3 py-3 text-left">利用者</th>
                  {activeItems.map(item => (
                    <th key={item.id} className="px-2 py-3 text-center">
                      <div>{item.name}</div>
                      <div className="mt-1 text-[11px] font-medium text-gray-500">{item.kind === 'charge' ? formatCurrency(item.unitPrice) : '区分'}</div>
                    </th>
                  ))}
                  <th className="px-2 py-3 text-right">請求合計</th>
                  <th className="px-2 py-3 text-left">備考</th>
                </tr>
                <tr className="border-t border-gray-200 bg-[#f7f8fa]">
                  <th className="sticky left-0 z-50 border-r border-gray-200 bg-[#f7f8fa] px-3 py-1 text-left text-[11px] font-medium text-gray-500">
                    品目を左右に移動
                  </th>
                  <th colSpan={activeItems.length + 2} className="bg-[#f7f8fa] p-0">
                    <div
                      className="sticky z-40 bg-[#f7f8fa]"
                      style={{ left: RESIDENT_COLUMN_WIDTH, width: horizontalViewportWidth }}
                    >
                      <input
                        type="range"
                        min={0}
                        max={maxHorizontalScroll}
                        step={1}
                        value={Math.min(horizontalScrollLeft, maxHorizontalScroll)}
                        onChange={syncTableHorizontalScroll}
                        className="supply-horizontal-range block h-5 w-full"
                        style={{ '--supply-scroll-thumb-width': `${horizontalThumbWidth}px` } as CSSProperties}
                        data-testid="supply-top-horizontal-scroll"
                        aria-label="品目列の横スクロール"
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map(row => (
                  <tr key={row.id} className="align-top transition hover:bg-[#fbfcfa]">
                    <td className="sticky left-0 z-10 border-r border-gray-100 bg-white px-3 py-3">
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 grid h-7 w-7 flex-none place-items-center rounded-lg bg-[#edf4ef] text-[11px] font-bold text-emerald-800">{rowNumberById.get(row.id)}</div>
                        <div className="min-w-0">
                          <div className="truncate font-bold text-gray-900">{row.residentName}</div>
                          <div className={`mt-0.5 truncate text-[11px] ${hasResidentKana({ name: row.residentName, kana: row.residentKana }) ? 'text-gray-400' : 'font-semibold text-amber-700'}`}>
                            {row.residentKana || 'ふりがな未登録'}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1 text-xs text-gray-500">
                            <span>{row.source === 'resident-master' ? 'マスター同期' : row.source === 'manual' ? '手入力' : 'Excel'}</span>
                            {row.residentStatus === 'retired' && row.active !== false && (
                              <span className="rounded-full bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-800 ring-1 ring-amber-200">
                                {formatDepartureDate(row.departureDate)}退居・退居月請求
                              </span>
                            )}
                            {residentView === 'retired' && (
                              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">{formatDepartureDate(row.departureDate)}退居</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    {activeItems.map(item => {
                      const value = row.values[item.id] ?? '';
                      const evaluated = evaluateCell(item, value);
                      const cellKey = getCellKey(row.id, item.id);
                      const mode = cellModes[cellKey] ?? inferCellMode(item, value);
                      const primaryMode = getPrimaryMode(item, mode);
                      const otherMode = getOtherMode(mode);
                      const amountValue = parseNumericAmount(value);
                      return (
                        <td key={item.id} className="px-2 py-2">
                          <select
                            disabled={residentView === 'retired'}
                            value={primaryMode}
                            onChange={event => updateCellPrimaryMode(row.id, item, event.target.value as CellPrimaryMode, value)}
                            className={`h-9 w-full rounded-md border px-1.5 text-center text-[11px] font-semibold outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 ${getCellClass(evaluated)}`}
                          >
                            {item.kind === 'descriptor' ? (
                              <>
                                <option value="blank">未</option>
                                <option value="large">大</option>
                                <option value="medium">中</option>
                                <option value="small">小</option>
                                <option value="other">他</option>
                              </>
                            ) : (
                              <>
                                <option value="blank">未</option>
                                <option value="circle">〇</option>
                                <option value="other">他</option>
                              </>
                            )}
                          </select>
                          {primaryMode === 'other' && (
                            <select
                              disabled={residentView === 'retired'}
                              value={item.kind === 'descriptor' ? 'note' : otherMode}
                              onChange={event => updateCellOtherMode(row.id, item, event.target.value as CellOtherMode, value)}
                              className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white px-1 text-center text-[11px] font-semibold text-slate-700 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                            >
                              {item.kind === 'descriptor' ? (
                                <option value="note">メモ</option>
                              ) : (
                                <>
                                  <option value="amount">金額</option>
                                  <option value="self">自分</option>
                                  <option value="hold">保留</option>
                                  <option value="note">メモ</option>
                                </>
                              )}
                            </select>
                          )}
                          {shouldShowAmountInput(mode) && (
                            <input
                              disabled={residentView === 'retired'}
                              type="number"
                              value={amountValue ?? ''}
                              onChange={event => updateCell(row.id, item.id, event.target.value)}
                              className="mt-1 h-8 w-full rounded-md border border-sky-200 bg-sky-50 px-1.5 text-right text-xs font-semibold text-sky-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                              placeholder="円"
                            />
                          )}
                          {shouldShowNoteInput(mode) && (
                            <input
                              disabled={residentView === 'retired'}
                              value={noteInputValue(item, value, mode)}
                              onChange={event => updateCell(row.id, item.id, event.target.value)}
                              className="mt-1 h-8 w-full rounded-md border border-amber-200 bg-amber-50 px-1.5 text-center text-xs font-semibold text-amber-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                              placeholder="例: 1月請求"
                            />
                          )}
                          {evaluated.amount > 0 && (
                            <div className="mt-1 text-right text-[11px] font-semibold text-gray-500">{formatCurrency(evaluated.amount)}</div>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-3 text-right">
                      <div className="text-base font-bold text-gray-900">{formatCurrency(rowTotal(row, chargeItems))}</div>
                    </td>
                    <td className="px-2 py-2">
                      <textarea
                        disabled={residentView === 'retired'}
                        value={row.memo}
                        onChange={event => updateMemo(row.id, event.target.value)}
                        rows={2}
                        className="min-h-[40px] w-full resize-y rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

    </main>
  );
};

const SummaryTile = ({ label, value, tone }: { label: string; value: string; tone: string }) => (
  <div className={`min-w-[132px] rounded-lg border px-3 py-2 ${tone}`}>
    <p className="text-xs font-semibold opacity-80">{label}</p>
    <p className="mt-1 text-xl font-bold leading-none">{value}</p>
  </div>
);

export default SupplySalesPage;
